import { ChevronRight, PenLine } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { SubjectBelief } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { VERDICT_COLOUR } from "@/lib/verdicts";

/**
 * One belief, as both surfaces render it.
 *
 * One component rather than two, deliberately. UI-16 says simple mode shows *the same
 * beliefs* with fewer affordances, and the way that stays true is that the rules live here
 * once: which flags a belief wears, that a superseded one is struck through and says what
 * replaced it, and that its provenance is the insights behind it with a link back to the
 * lines each was read from. Two renderers would be two places for "contradicted" to stop
 * meaning the same thing.
 *
 * What the variant changes is only what is *offered*:
 *
 * - `full` (UI-10) — the provenance opens inline, each insight carrying its verdict word
 *   and a link into the ledger. This is the screen where somebody audits the extractor.
 * - `simple` (UI-16) — larger type, and the provenance behind a barely-visible source
 *   label that opens a popover with the quotes. No verdict words and no ledger: the six
 *   words are internal here, and a reader who has never been told what "supersede" means
 *   should not be shown it as a badge.
 */
export function Belief({
  belief,
  slug,
  variant = "full",
}: {
  belief: SubjectBelief;
  /** The subject being read, so "superseded by" only links away when it lands elsewhere. */
  slug: string;
  variant?: "full" | "simple";
}) {
  const [open, setOpen] = useState(false);

  const uncertain = belief.confidence !== null && belief.confidence < 0.5;
  // One row per source rather than per insight: three insights from one meeting is one
  // place this was said, and the label says where, not how many times.
  const sources = [...new Map(belief.insights.map((insight) => [insight.sourceMaterialId, insight])).values()];

  const flags = (
    <div className="flex flex-wrap items-center gap-2 text-[length:var(--text-2xs)]">
      {/* WORLD-8 — provenance stays visible everywhere else in the app, and a belief typed
          in by hand is not the place to start hiding where it came from. */}
      {belief.assertedByUser && <Flag label="Asserted by you" colour="var(--vl-bridge-soft)" icon={PenLine} />}
      {belief.contradicted && <Flag label="Contradicted" colour="var(--destructive)" />}
      {uncertain && <Flag label="Uncertain" colour="var(--warning)" />}
      {belief.validTo && <Flag label="Superseded" colour="var(--text-muted)" />}
    </div>
  );

  const statement = (
    <p
      className={cn(
        variant === "simple"
          ? "text-[17px] leading-[1.75] text-[var(--text-primary)] [text-wrap:pretty]"
          : "max-w-[62ch] text-[15px] leading-[1.7] text-[var(--text-primary)]",
        belief.validTo && "line-through decoration-[var(--text-muted)]",
      )}
    >
      {belief.statement}
    </p>
  );

  if (variant === "simple") {
    return (
      <div className="flex flex-col gap-2">
        {flags}
        {statement}

        {sources.length > 0 && (
          <div className="relative">
            {/* The quiet source label: 50 % opacity until it is wanted. Provenance is not
                removed in simple mode — it is put where somebody who wants it can find it
                and somebody who does not is not asked to read it. */}
            <button
              type="button"
              onClick={() => setOpen((shown) => !shown)}
              aria-expanded={open}
              className="p-0 text-[length:var(--text-2xs)] text-[var(--text-muted)] opacity-50 transition-opacity hover:opacity-100"
            >
              {sourceLabel(sources)}
            </button>

            {open && (
              <div className="absolute left-0 top-[26px] z-40 flex w-[440px] max-w-[88vw] flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-3)] p-[var(--space-4)] shadow-[var(--shadow-lg)]">
                <p className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
                  Where this came from
                </p>
                {belief.insights.map((insight) => (
                  <div key={insight.id} className="flex flex-col gap-0.5">
                    <p className="text-[length:var(--text-sm)] leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]">
                      “{insight.text}”
                    </p>
                    <p className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                      {insight.sourceTitle ?? "Untitled source"}
                      {insight.sourceDate && ` — ${formatDate(insight.sourceDate)}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {belief.supersededByStatement && (
          <p className="text-[length:var(--text-xs)] text-[var(--text-muted)] [text-wrap:pretty]">
            Replaced by “{belief.supersededByStatement}”
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {statement}
      {flags}

      <p className="flex flex-wrap items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {sources.length > 0 && (
          <>
            <span>Said in</span>
            <Link
              to={sourceHref(sources[0]!.sourceKind, sources[0]!.sourceMaterialId, sources[0]!.id)}
              className="text-[var(--text-secondary)] underline underline-offset-2 hover:text-[var(--vl-life)]"
            >
              {sources[0]!.sourceTitle ?? "Untitled source"}
            </Link>
            {sources.length > 1 && <span>and {sources.length - 1} more</span>}
            <ChevronRight size={11} strokeWidth={2} aria-hidden />
          </>
        )}

        {/* WORLD-8/UI-20 — a belief the owner typed has no words behind it but theirs, so there
            is nothing to disclose; what it does carry is when the owner said it started holding. */}
        {belief.insights.length === 0 && belief.assertedByUser ? (
          <span>Holding since {formatDate(belief.validFrom)}</span>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((shown) => !shown)}
            aria-expanded={open}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            The words behind this ({belief.insights.length})
          </button>
        )}
      </p>

      {open && (
        <ul className="ml-1 flex flex-col gap-2.5 border-l border-[var(--border-subtle)] pl-[var(--space-4)]">
          {belief.insights.map((insight) => (
            <li key={insight.id} className="flex flex-col gap-1">
              <span className="max-w-[62ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                “{insight.text}”
              </span>
              <span className="flex flex-wrap items-center gap-2 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                <span
                  className="rounded-full border px-2 py-0.5 font-semibold"
                  style={{ borderColor: VERDICT_COLOUR[insight.verdict], color: VERDICT_COLOUR[insight.verdict] }}
                >
                  {insight.verdict}
                </span>
                <Link
                  to={sourceHref(insight.sourceKind, insight.sourceMaterialId, insight.id)}
                  className="underline underline-offset-2 hover:text-[var(--text-primary)]"
                >
                  {insight.sourceTitle ?? "Untitled source"}
                </Link>
                {insight.sourceDate && <span>{formatDate(insight.sourceDate)}</span>}

                {/* UI-7's world-view direction, complete: the source link jumps to the lines
                    this insight was read from, and this one opens its row in the ledger —
                    where everything else it changed is listed. The two answer different
                    questions, so both are offered rather than one standing in for the other.
                    The window is widened in the address rather than left to the ledger to
                    infer, because a belief's evidence may be months old and since discarded,
                    and an address should say what it shows. */}
                <Link
                  to={`/insights?horizon=all&state=all&insight=${insight.id}`}
                  className="underline underline-offset-2 hover:text-[var(--text-primary)]"
                >
                  In the ledger
                </Link>
              </span>
            </li>
          ))}
        </ul>
      )}

      {belief.supersededByStatement && (
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Superseded by “{belief.supersededByStatement}”
          {belief.supersededBySlug && belief.supersededBySlug !== slug && belief.supersededByTitle && (
            <>
              {" on "}
              <Link
                to={`/world-view/${belief.supersededBySlug}`}
                className="underline underline-offset-2 hover:text-[var(--text-primary)]"
              >
                {belief.supersededByTitle}
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}

/** "Klantgesprek +2 · 21 Jul 2026" — where this was said, in one quiet line. */
function sourceLabel(sources: SubjectBelief["insights"]): string {
  const first = sources[0]!;
  const extra = sources.length - 1;
  const named = (first.sourceTitle ?? "Untitled source") + (extra > 0 ? ` +${extra}` : "");

  return first.sourceDate ? `${named} · ${formatDate(first.sourceDate)}` : named;
}

/**
 * Where a source opens. A recording has its own screen with a transcript to highlight in;
 * anything else is a source detail — the same split the review queue makes, so one insight
 * does not land in two different places depending on which screen you reached it from.
 */
export function sourceHref(kind: string, id: string, insightId?: string): string {
  const base = kind === "recording" ? `/recordings/${id}` : `/sources/${id}`;
  return insightId ? `${base}?insight=${insightId}` : base;
}

function Flag({
  label,
  colour,
  icon: Icon,
}: {
  label: string;
  colour: string;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold"
      style={{ borderColor: colour, color: colour }}
    >
      {Icon && <Icon size={10} strokeWidth={2.5} />}
      {label}
    </span>
  );
}
